import { useState } from 'react';
import { CheckCircle, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const INDIAN_STATES = [
  'Andaman and Nicobar Islands', 'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chandigarh', 'Chhattisgarh',
  'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jammu and Kashmir',
  'Jharkhand', 'Karnataka', 'Kerala', 'Ladakh', 'Lakshadweep', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya',
  'Mizoram', 'Nagaland', 'Odisha', 'Puducherry', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
  'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
];
const YEARS = ['First Year', 'Second Year', 'Third Year', 'Fourth Year', 'Fifth Year', 'Other'];

const EMPTY = {
  full_name: '', email: '', whatsapp: '', state: '', city: '', college: '', course: '', year_of_study: '',
  linkedin: '', clubs: '', why: '', engage: '', acknowledgement: false, privacy_consent: false, website: '',
};

const input = 'w-full px-4 py-3 border border-slate-200 rounded-xl bg-white focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-shadow';

const validate = (f) => {
  if (f.full_name.trim().length < 2) return 'Please enter your full name';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) return 'Please enter a valid email address';
  if (!/^\+?[0-9 ]{10,15}$/.test(f.whatsapp.trim())) return 'Please enter a valid WhatsApp number';
  if (!f.state) return 'Please select your state';
  if (f.city.trim().length < 2) return 'Please enter your city';
  if (f.college.trim().length < 2) return 'Please enter your college/university name';
  if (f.course.trim().length < 2) return 'Please enter your course/degree';
  if (!f.year_of_study) return 'Please select your current year of study';
  if (f.linkedin.trim() && !/^(https?:\/\/)?([a-z]{2,3}\.)?linkedin\.com\/.+/i.test(f.linkedin.trim())) return 'Please enter a valid LinkedIn profile URL';
  if (f.why.trim().length < 20) return 'Please tell us why you would like to become a Campus Ambassador (at least 20 characters)';
  if (f.engage.trim().length < 20) return 'Please tell us how you would engage students (at least 20 characters)';
  if (!f.acknowledgement) return 'Please confirm you understand the role conditions';
  if (!f.privacy_consent) return 'Please agree to the processing of your application details';
  return '';
};

const Field = ({ label, required, children, hint }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">{label} {required && <span className="text-red-500">*</span>}</label>
    {children}
    {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
  </div>
);

const Success = () => (
  <div data-testid="ca-success" className="bg-white rounded-3xl border border-slate-200 shadow-lg p-8 md:p-10 text-center">
    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle className="w-8 h-8 text-green-600" /></div>
    <h2 className="text-2xl font-bold text-slate-900">Application Submitted Successfully!</h2>
    <div className="mt-4 space-y-3 text-slate-600 max-w-lg mx-auto">
      <p>Thank you for your interest in becoming an Elevate Careers Campus Ambassador.</p>
      <p>We've received your application. Our team will review it and contact shortlisted applicants regarding the next steps.</p>
      <p>We appreciate your interest in helping students build better careers.</p>
    </div>
    <a href="/" data-testid="ca-back-home-btn" className="inline-block mt-8 px-6 py-3 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-medium transition-colors">Back to Homepage</a>
  </div>
);

export const CampusAmbassadorForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    const msg = validate(form);
    if (msg) { setError(msg); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/campus-ambassador/apply`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Please check your details and try again.');
      setDone(true);
      document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } catch (err) { setError(err.message || 'Something went wrong. Please try again.'); } finally { setSubmitting(false); }
  };

  return (
    <section id="apply" className="bg-slate-50 py-16 md:py-20 scroll-mt-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6">
        {done ? <Success /> : (
          <div className="bg-white rounded-3xl border border-slate-200 shadow-lg p-6 md:p-10">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900">Apply to Become a Campus Ambassador</h2>
            <p className="mt-2 text-slate-600">Tell us a little about yourself and why you'd like to represent Elevate Careers at your college. No previous experience or resume required.</p>
            <form onSubmit={submit} noValidate className="mt-8 space-y-4" data-testid="ca-form">
              <input type="text" name="website" value={form.website} onChange={set} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" />
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Full Name" required><input name="full_name" value={form.full_name} onChange={set} className={input} data-testid="ca-name-input" /></Field>
                <Field label="Email Address" required><input type="email" name="email" value={form.email} onChange={set} className={input} data-testid="ca-email-input" /></Field>
                <Field label="WhatsApp Number" required><input type="tel" name="whatsapp" value={form.whatsapp} onChange={set} className={input} data-testid="ca-whatsapp-input" /></Field>
                <Field label="State" required>
                  <select name="state" value={form.state} onChange={set} className={input} data-testid="ca-state-select">
                    <option value="">Select your state</option>
                    {INDIAN_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                <Field label="City" required><input name="city" value={form.city} onChange={set} className={input} data-testid="ca-city-input" /></Field>
                <Field label="College/University Name" required><input name="college" value={form.college} onChange={set} className={input} data-testid="ca-college-input" /></Field>
                <Field label="Course/Degree" required><input name="course" value={form.course} onChange={set} placeholder="e.g. B.Com, B.Tech CSE, MBA" className={input} data-testid="ca-course-input" /></Field>
                <Field label="Current Year of Study" required>
                  <select name="year_of_study" value={form.year_of_study} onChange={set} className={input} data-testid="ca-year-select">
                    <option value="">Select your year</option>
                    {YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
                  </select>
                </Field>
              </div>
              <Field label="LinkedIn Profile"><input name="linkedin" value={form.linkedin} onChange={set} placeholder="https://linkedin.com/in/your-profile (optional)" className={input} data-testid="ca-linkedin-input" /></Field>
              <Field label="Are you involved in any student clubs, societies or campus activities?">
                <input name="clubs" value={form.clubs} onChange={set} maxLength={300} placeholder="Optional" className={input} data-testid="ca-clubs-input" />
              </Field>
              <Field label="Why would you like to become an Elevate Campus Ambassador?" required hint={`${form.why.length}/1500`}>
                <textarea name="why" value={form.why} onChange={set} rows={4} maxLength={1500} className={`${input} resize-none`} data-testid="ca-why-input" />
              </Field>
              <Field label="How would you engage students at your college?" required hint={`${form.engage.length}/1500`}>
                <textarea name="engage" value={form.engage} onChange={set} rows={4} maxLength={1500} className={`${input} resize-none`} data-testid="ca-engage-input" />
              </Field>
              <div className="space-y-3 pt-2">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="acknowledgement" checked={form.acknowledgement} onChange={set} className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600" data-testid="ca-ack-checkbox" />
                  <span className="text-sm text-slate-700">I understand that this is a part-time, performance-incentivized opportunity without a fixed stipend, requiring approximately 2–3 hours of participation per week. <span className="text-red-500">*</span></span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="privacy_consent" checked={form.privacy_consent} onChange={set} className="mt-1 w-4 h-4 rounded border-slate-300 text-teal-600" data-testid="ca-privacy-checkbox" />
                  <span className="text-sm text-slate-700">I agree to Elevate Careers processing my application details for recruitment and selection purposes, as described in the <a href="/privacy" target="_blank" rel="noreferrer" className="text-teal-600 underline" data-testid="ca-privacy-link">Privacy Policy</a>. <span className="text-red-500">*</span></span>
                </label>
              </div>
              {error && <div data-testid="ca-form-error" className="p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-600">{error}</div>}
              <button type="submit" disabled={submitting} data-testid="ca-submit-btn"
                className="w-full py-4 rounded-full bg-teal-600 hover:bg-teal-500 text-white font-semibold transition-colors flex items-center justify-center gap-2 disabled:opacity-50">
                {submitting ? <><Loader2 className="w-5 h-5 animate-spin" /> Submitting...</> : 'Submit Application'}
              </button>
            </form>
          </div>
        )}
      </div>
    </section>
  );
};
